/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import React from "react";
import { ArticleComponentsFragment, CosmosCalloutCardFragment } from "@cosmos/hygraph-schema";

import {
	Animate,
	ArticleChildrenWrap,
	ArticleContainer,
	AssetCarouselSection,
	AssetGallerySection,
	ButtonList,
	Caption,
	ContentLargeImageSection,
	CosmosAsset,
	CosmosCompare,
	CosmosGitHubGist,
	CosmosRichText,
	CosmosEmbed,
	DividerSection,
	IconLinkGroup,
	Interview,
	LogoMarquee,
	LogoMultiCardsSection,
	NewsMediaCardsSection,
	SectionHeading,
	SlimContactCard,
	UserProfile,
	Wrap,
	Quote,
	CosmosCalloutCard,
	TitleDescStack,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { useCosmosComponentContext } from "@components/context";

const textEditorStyles = css({
	"& h1": {
		textStyle: "heading2XL",
	},
	"& h2": {
		textStyle: "headingXL",
	},
	"& h3": {
		textStyle: "headingLG",
	},
	"& h4": {
		textStyle: "headingMD",
	},
	"& h5": {
		textStyle: "headingSM",
	},
	"& h6": {
		textStyle: "eyebrowLG",
	},
	"& h1, & h2, & h3, & h4, & h5, & h6": {
		marginBlock: "eds.dimension.8",
		textWrap: "wrap",
	},
	"& hr": {
		marginBlockStart: "revert",
		marginBlockEnd: "revert",
	},
	"& img": {
		margin: "0 auto",
	},
});

export interface ArticleComponentsSectionProps {
	articleComponents?: ArticleComponentsFragment[] | null;
	nAnimatedAsset?: number;
}

export const ArticleComponentsSection = ({
	articleComponents,
	nAnimatedAsset = 0,
}: ArticleComponentsSectionProps): React.JSX.Element | null => {
	const { LoadingComponent } = useCosmosComponentContext();
	if (!articleComponents?.length) return null;

	const richTextStyles = css({
		"&&": {
			textStyle: "paragraphMD",
		},
		"& h1": {
			color: "red",
			fontSize: "unset",
		},
	});

	let assetIdx = -1;
	return (
		<>
			{articleComponents.map((component, i) => {
				assetIdx++;
				switch (component.__typename) {
					case "CosmosElementAsset":
						return (
							<ArticleContainer size="md" key={`article-section-${i}`}>
								<Wrap
									condition={assetIdx < nAnimatedAsset}
									wrap={(children) => (
										<Animate delay={0.25} type="slideLeft">
											{children}
										</Animate>
									)}
								>
									<Caption caption={component?.caption}>
										<CosmosAsset
											asset={component?.articleComponentAsset}
											className={css({
												borderRadius: "eds.dimension.24",
												margin: "0 auto",
											})}
										/>
									</Caption>
								</Wrap>
							</ArticleContainer>
						);
					case "CosmosElementButtonList":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<ButtonList {...component} />
							</ArticleContainer>
						);
					case "CosmosElementCkEditorText":
						return (
							<ArticleContainer key={`article-section-${i}`} className={cx(textEditorStyles)}>
								<CosmosRichText html={component?.ckText} className={richTextStyles} />
							</ArticleContainer>
						);
					case "CosmosElementGithubGist":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<CosmosGitHubGist {...component} LoadingComponent={LoadingComponent} />
							</ArticleContainer>
						);
					case "CosmosElementIconLinkGroup":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<IconLinkGroup {...component} />
							</ArticleContainer>
						);
					case "CosmosElementInterview":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<Interview {...component} />
							</ArticleContainer>
						);
					case "CosmosElementRichText":
						return (
							<ArticleContainer key={`article-section-${i}`} className={cx(textEditorStyles)}>
								<CosmosRichText html={component?.text?.html} className={richTextStyles} />
							</ArticleContainer>
						);
					case "CosmosElementSectionHeading":
						return (
							<ArticleContainer size="md" key={`article-section-${i}`}>
								<SectionHeading {...component} />
							</ArticleContainer>
						);
					case "CosmosElementLicenseContactCard":
						return (
							<ArticleContainer size="md" key={`article-section-${i}`}>
								<SlimContactCard {...component} />
							</ArticleContainer>
						);
					case "CosmosElementVideoEmbed":
						return (
							<ArticleContainer size="md" key={`article-section-${i}`}>
								<CosmosEmbed {...component} />
							</ArticleContainer>
						);
					case "CosmosSectionAssetCarousel":
						return (
							<ArticleChildrenWrap size="md" key={`article-section-${i}`}>
								<AssetCarouselSection {...component} />
							</ArticleChildrenWrap>
						);
					case "CosmosSectionAssetGallery":
						return (
							<ArticleChildrenWrap size="md" key={`article-section-${i}`}>
								<AssetGallerySection {...component} />
							</ArticleChildrenWrap>
						);
					case "CosmosSectionContentLargeImage":
						return (
							<ContentLargeImageSection
								sectionContainerProps={{
									disablePaddingBlock: true,
									className: css({
										paddingBlock: "eds.dimension.64",
										smDown: {
											paddingBlock: "eds.dimension.48",
										},
									}),
								}}
								key={`article-section-${i}`}
								{...component}
							/>
						);
					case "CosmosSectionLogoList":
						return <LogoMarquee key={`article-section-${i}`} className={css({ width: "100vw" })} {...component} />;
					case "CosmosSectionLogoMultiCard":
						return (
							<ArticleChildrenWrap
								size="md"
								className={css({ "& .section-container > .d_flex": { xxl: { columnGap: "eds.dimension.32" } } })}
								key={`article-section-${i}`}
							>
								<LogoMultiCardsSection {...component} />
							</ArticleChildrenWrap>
						);
					case "CosmosSectionNewsMediaCard":
						return (
							<ArticleChildrenWrap size="md" key={`article-section-${i}`}>
								<NewsMediaCardsSection {...component} />
							</ArticleChildrenWrap>
						);
					case "CosmosElementCompare":
						return (
							<ArticleContainer size="md" key={`article-section-${i}`}>
								<CosmosCompare {...component} />
							</ArticleContainer>
						);
					case "CosmosElementUserProfile":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<UserProfile {...component} />
							</ArticleContainer>
						);
					case "CosmosSectionDivider":
						return (
							<div className={css({ paddingBlock: "eds.dimension.32" })} key={`article-section-divider-${i}`}>
								<DividerSection {...component} />
							</div>
						);
					case "CosmosElementQuote":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<Quote {...component} />
							</ArticleContainer>
						);
					case "CosmosElementCalloutCard":
						return (
							<CosmosCalloutCard
								wholePage={articleComponents.length === 1}
								{...(component as CosmosCalloutCardFragment)}
								key={`article-section-${i}`}
							/>
						);
					case "CosmosElementTitleAndDesc":
						return (
							<ArticleContainer key={`article-section-${i}`}>
								<TitleDescStack
									stack={{ gap: "eds.dimension.16" }}
									title={{ ...component.title, variant: "headingXL" }}
									description={{ html: component.desc, variant: "paragraphMD", color: "eds.text.primary" }}
								/>
							</ArticleContainer>
						);
					default:
						return null;
				}
			})}
		</>
	);
};
